<script setup lang="ts">
import { computed } from 'vue'
import type { UseCaseArticleBudget } from '@/data/useCases'
import { formatTokens, poundsFor } from '@/composables/articleLabel'

// The money, at a glance: one pack as a meter, split by the article's parts, with what
// is left over. Segment widths come from the parts' summed chapter `spent`.
const props = defineProps<{ budget: UseCaseArticleBudget; groups: { id: string; title?: string; spent: number }[] }>()

const tints = ['bg-brass-400', 'bg-primary-300', 'bg-surface-300']

const segments = computed(() =>
  props.groups
    .flatMap((g) => (g.title && g.spent > 0 ? [{ id: g.id, title: g.title, spent: g.spent }] : []))
    .map((s, i) => ({ ...s, tint: tints[i % tints.length] })),
)
const left = computed(() => Math.max(0, props.budget.pack.tokens - segments.value.reduce((sum, s) => sum + s.spent, 0)))
const pct = (n: number) => `${(n / props.budget.pack.tokens) * 100}%`
const pounds = (n: number) => poundsFor(n, props.budget.pack, { approx: false })
</script>

<template>
  <div class="rounded-card border border-surface-800 bg-surface-900 p-4 sm:p-5 mb-6">
    <p class="text-xs uppercase tracking-wide text-surface-400 mb-1">{{ budget.eyebrow }}</p>
    <p class="text-xl sm:text-2xl font-bold text-surface-50 leading-snug">{{ budget.headline }}</p>

    <div class="mt-4 flex h-3 w-full overflow-hidden rounded-full bg-surface-800" role="img" :aria-label="`${budget.pack.name}: ${formatTokens(budget.pack.tokens)} tokens, ${formatTokens(left)} left over`">
      <div v-for="s in segments" :key="s.id" class="h-full shrink-0" :class="s.tint" :style="{ width: pct(s.spent) }" />
    </div>
    <ul class="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-surface-300">
      <li v-for="s in segments" :key="s.id" class="flex items-center gap-2">
        <span class="h-2.5 w-2.5 shrink-0 rounded-full" :class="s.tint" aria-hidden="true" />
        <span>{{ s.title }} <span class="font-semibold text-brass-400">{{ pounds(s.spent) }}</span> <span class="text-surface-400">{{ formatTokens(s.spent) }} tokens</span></span>
      </li>
      <li class="flex items-center gap-2">
        <span class="h-2.5 w-2.5 shrink-0 rounded-full bg-surface-800 ring-1 ring-surface-600" aria-hidden="true" />
        <span>{{ budget.leftLabel }} <span class="font-semibold text-brass-400">{{ pounds(left) }}</span> <span class="text-surface-400">{{ formatTokens(left) }} tokens</span></span>
      </li>
    </ul>

    <dl class="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
      <div v-for="tile in budget.tiles" :key="tile.label" class="flex flex-row-reverse justify-end items-baseline gap-3 sm:flex-col-reverse sm:items-start sm:gap-0">
        <dt class="text-sm text-surface-400 leading-snug">{{ tile.label }}</dt>
        <dd class="text-2xl sm:text-3xl font-bold text-brass-400">{{ tile.value }}</dd>
      </div>
    </dl>
  </div>
</template>
